import React, { useEffect, useState } from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

import AppForm from "components/Shared/AppForm";
import { useShipmentFulfillmentDetailContext } from "contexts/ShipmentFulfillmentContext";
import { getDateTimeAuto } from "utils/dateFormats";

const INITIAL_STATE = {
  stopLocation: { location: "", lat: null, lon: null },
  stopTime: getDateTimeAuto(),
  status: "Pending",
};

const CollectionStopsForm = ({ isBusy, shipmentStop, onSubmit, onCancel }) => {
  const { fulfillmentDetail } = useShipmentFulfillmentDetailContext();
  const { control, handleSubmit, reset, setValue } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      stopTime: getDateTimeAuto(),
      collectionId: fulfillmentDetail?._id,
    },
  });

  // Fix: Correctly destructure useState to get state and setter
  const [ setLat] = useState(null);
  const [ setLon] = useState(null);

  useEffect(() => {
    if (shipmentStop) {
      reset({
        stopLocation: shipmentStop.stopLocation ?? { location: "", lat: null, lon: null },
        stopTime: new Date(shipmentStop.stopTime).toISOString().slice(0, 16),
        status: shipmentStop.status ?? "Pending",
      });
      setLat(shipmentStop.stopLocation?.lat ?? null);
      setLon(shipmentStop.stopLocation?.lon ?? null);
    }
  }, [shipmentStop, reset, setLat, setLon]);

  const reverseGeocode = async (latitude, longitude) => {
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
      );
      const data = await response.json();

      const address = data.address;
      let locationName = "";

      if (address.neighbourhood) {
        locationName = address.neighbourhood;
      } else if (address.suburb) {
        locationName = address.suburb;
      } else if (address.city_district) {
        locationName = address.city_district;
      }

      if (address.city && locationName !== address.city) {
        locationName = locationName ? `${locationName}, ${address.city}` : address.city;
      } else if (address.town) {
        locationName = locationName ? `${locationName}, ${address.town}` : address.town;
      }

      return locationName || "Current Location";
    } catch (error) {
      console.error("Reverse geocoding error:", error);
      return "Current Location";
    }
  };

  const handleGetCurrentLocation = async () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const currentLat = position.coords.latitude;
          const currentLon = position.coords.longitude;
          setLat(currentLat);
          setLon(currentLon);

          const locationName = await reverseGeocode(currentLat, currentLon);

          setValue("stopLocation.location", locationName);
          setValue("stopLocation.lat", currentLat);
          setValue("stopLocation.lon", currentLon);

          toast.success("Location fetched successfully!");
        },
        (error) => {
          toast.error("Failed to fetch location: " + error.message);
        }
      );
    } else {
      toast.error("Geolocation is not supported by this browser.");
    }
  };

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Controller
              name="stopLocation.location"
              control={control}
              rules={{ required: "Location is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Stop Location"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="stopLocation.lat"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Latitude"
                  type="number"
                  value={field.value ?? ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="stopLocation.lon"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Longitude"
                  type="number"
                  value={field.value ?? ""}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name="stopTime"
              control={control}
              rules={{ required: "Stop Time is required" }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Stop Time"
                  type="datetime-local"
                  InputLabelProps={{ shrink: true }}
                  value={field.value ? field.value.slice(0, 16) : getDateTimeAuto()}
                  error={!!error}
                  helperText={error?.message}
                  disabled={isBusy}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )}
            />
          </Grid>
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button disabled={isBusy} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isBusy}
        >
          {shipmentStop ? "Update" : "Add"}
        </Button>
        <Button
          variant="outlined"
          onClick={handleGetCurrentLocation}
          disabled={isBusy}
        >
          Get Current Location
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default CollectionStopsForm;